import { useEffect, useState } from "react";
import { toKebabCase } from "@miplatform/utils";
import { mdiChevronRight, mdiDotsVertical } from "@mdi/js";
import {
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Popper,
  Typography,
  useTheme,
} from "@mui/material";
import { MiIcon } from "../MiIcon/MiIcon";
import { useMiMenuController } from "./useMiMenuController";

/**
 * MiMenu component renders a customizable dropdown menu with optional icons, dividers, and nested poppers.
 *
 * @param {Object} props - The props for MiMenu.
 * @param {Function} [props.closeContextMenu] - Callback to close the context menu.
 * @param {Function} [props.onMenuOpen] - Callback when the menu is opened.
 * @param {Array} [props.data=[]] - Array of menu item objects to render.
 * @param {boolean} [props.condensed=false] - If true, renders a condensed version of the menu.
 * @param {boolean} [props.disabled=false] - If true, disables the menu.
 * @param {boolean} [props.hasDivider] - If true, adds a divider between menu items.
 * @param {string} [props.iconColor] - Color for the menu icon.
 * @param {boolean} [props.showHover] - If true, shows hover effect on the menu toggler.
 * @param {string} [props.menuItemStyles] - Additional className/styles for menu items.
 * @param {Object} [props.menuStyles] - Inline styles for the menu.
 * @param {React.ReactNode} [props.optionalElement] - Optional element to render as the menu toggler.
 * @param {string} [props.selectedColor] - Color for the selected menu item.
 * @param {string} [props.selectedItem] - Label of the currently selected menu item.
 * @param {string} [props.testID] - Test ID prefix for testing.
 * @param {boolean} [props.showCloseDropdown] - If true, shows a "Close dropdown" menu item.
 * @param {boolean} [props.boldText] - If true, renders menu item text in bold.
 * @param {HTMLElement|null} [props.anchorElement] - Element to anchor the menu to.
 * @param {boolean} [props.open] - If true, keeps the menu open.
 * @param {Object|null} [props.anchorPosition] - Position to anchor the menu to (if not using anchorElement).
 * @param {...Object} [menuProps] - Additional props passed to the MUI Menu component.
 * @returns {JSX.Element|null} The rendered MiMenu component or null if no data is provided.
 */
export function MiMenu({
  closeContextMenu,
  onMenuOpen,
  data = [],
  condensed = false,
  disabled = false,
  hasDivider,
  iconColor,
  showHover,
  showCloseDropdown,
  menuItemStyles,
  menuStyles,
  popperStyles,
  optionalElement,
  selectedColor,
  selectedItem,
  testID,
  boldText,
  open,
  anchorPosition = null,
  ...menuProps
}) {
  const [popperAnchor, setPopperAnchor] = useState(null);
  const [activeAction, setActiveAction] = useState(null);

  const theme = useTheme();

  const { anchorEl, openMenu, closeMenu, handleAction } = useMiMenuController({
    disabled,
    onMenuOpen,
    closeContextMenu,
  });

  const handleMouseEnter = (event, action) => {
    if (action.popperContent || action.items) {
      setPopperAnchor(event.currentTarget);
      setActiveAction(action);
    } else if (activeAction) {
      setPopperAnchor(null);
      setActiveAction(null);
    }
  };

  const handleMouseLeave = () => {
    if (!activeAction?.items) {
      setPopperAnchor(null);
      setActiveAction(null);
    }
  };

  useEffect(() => {
    setActiveAction(null);
    setPopperAnchor(null);
  }, [open]);

  if (!data) {
    return null;
  }

  const renderItem = (item, isSubItem) => (
    <Box
      key={item.label}
      onMouseEnter={isSubItem ? undefined : (e) => handleMouseEnter(e, item)}
      onMouseLeave={handleMouseLeave}
    >
      <MenuItem
        className={menuItemStyles}
        style={condensed ? { padding: theme.spacing(1, 2) } : {}}
        selected={selectedItem && selectedItem === item.label}
        onClick={(event) => handleAction(event, item)}
        data-testid={
          item.testID ||
          item["data-testid"] ||
          `${testID}-${toKebabCase(item.label)}`
        }
        disabled={item.disabled ?? false}
      >
        {item.customIcon}
        {item.icon && (
          <ListItemIcon style={{ minWidth: condensed ? 24 : 36 }}>
            <MiIcon
              path={item.icon}
              size={condensed ? 0.8 : 1}
              color={item.color}
              data-testid="svg-icon"
            />
          </ListItemIcon>
        )}
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{
            fontSize: condensed ? theme.fontSize.small : theme.fontSize.medium,
            fontWeight: boldText
              ? theme.fontWeight.medium
              : theme.fontWeight.regular,
          }}
        />
        {item.keepMenuOpen && (
          <ListItemIcon style={{ minWidth: 18, marginLeft: theme.spacing(2) }}>
            <MiIcon path={mdiChevronRight} size={condensed ? 0.8 : 1} />
          </ListItemIcon>
        )}
      </MenuItem>
      {hasDivider && <Divider />}
    </Box>
  );

  return (
    <>
      <Box
        onClick={openMenu}
        data-testid={`${testID}-toggler`}
        sx={
          showHover && !disabled
            ? {
                ":hover": {
                  backgroundColor: theme.palette.grey.light,
                  borderRadius: 1,
                },
              }
            : {}
        }
      >
        {optionalElement || (
          <MiIcon
            path={mdiDotsVertical}
            size={condensed ? 0.6 : 1}
            style={{
              cursor: !disabled ? "pointer" : "inherit",
              display: "flex",
            }}
            color={
              disabled
                ? theme.palette.grey.darkAccent
                : iconColor || theme.palette.grey.main
            }
          />
        )}
      </Box>
      <Menu
        elevation={1}
        anchorEl={anchorEl}
        keepMounted={!closeContextMenu}
        open={open ?? Boolean(anchorEl)}
        onClose={closeMenu}
        style={menuStyles}
        data-testid="mi-menu"
        sx={{
          "& .MuiList-root.MuiMenu-list": {
            padding: theme.spacing(showCloseDropdown ? 0 : 1, 0),
          },
        }}
        anchorPosition={anchorPosition}
        anchorReference={anchorPosition ? "anchorPosition" : "anchorEl"}
        {...menuProps}
      >
        <Popper
          open={
            Boolean(popperAnchor) &&
            Boolean(activeAction?.popperContent || activeAction?.items)
          }
          anchorEl={popperAnchor}
          placement="right-start"
          sx={{ zIndex: theme.zIndex.tooltip + 1, ...popperStyles }}
        >
          <Paper>
            {activeAction?.popperContent ||
              activeAction?.items.map((item) => renderItem(item, true))}
          </Paper>
        </Popper>
        {data.map((item) => !item.hide && renderItem(item))}
        {showCloseDropdown && (
          <MenuItem
            onClick={closeMenu}
            style={{
              backgroundColor: theme.palette.grey.lightAccent,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            data-testid={`${testID}-close-dropdown`}
          >
            <Typography
              fontSize={theme.fontSize.xsmall}
              fontWeight={theme.fontWeight.medium}
            >
              Close dropdown
            </Typography>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
